"use client"

import React, { useState } from "react"
import Link from "next/link"
import { Mail, CheckCircle2, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AuthCardShell } from "@/components/auth/auth-card-shell"
import axiosInstance from "@/lib/axios-client"
import { API_ENDPOINTS } from "@/lib/api-endpoints"

// Definim tipurile pentru props
interface ForgotPasswordFormProps {
    dict: {
        back_to_login: string
        title_input: string
        title_success: string
        subtitle_input: string
        subtitle_success: string
        email_label: string
        email_placeholder: string
        submit_btn: string
        sending_btn: string
        spam_notice: string
        success_back_btn: string
    }
    lang: string
}

export function ForgotPasswordForm({ dict, lang }: ForgotPasswordFormProps) {
    const [isLoading, setIsLoading] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [email, setEmail] = useState("")

    async function onSubmit(event: React.SyntheticEvent) {
        event.preventDefault()
        setIsLoading(true)

        try {
            await axiosInstance.post(API_ENDPOINTS.FORGOT_PASSWORD, { email })
        } catch {
            // Always show success to prevent email enumeration
        } finally {
            setIsLoading(false)
            setIsSubmitted(true)
        }
    }

    return (
        <AuthCardShell
            title={isSubmitted ? dict.title_success : dict.title_input}
            description={isSubmitted ? dict.subtitle_success : dict.subtitle_input}
            backHref="/login"
            backLabel={dict.back_to_login}
        >
                {!isSubmitted ? (
                    // STAREA 1: Formularul de input
                    <form onSubmit={onSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">{dict.email_label}</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                                <Input
                                    id="email"
                                    placeholder={dict.email_placeholder}
                                    type="email"
                                    autoCapitalize="none"
                                    autoComplete="email"
                                    autoCorrect="off"
                                    className="pl-9"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </div>
                        </div>
                        <Button className="w-full" type="submit" disabled={isLoading}>
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {dict.sending_btn}
                                </>
                            ) : (
                                dict.submit_btn
                            )}
                        </Button>
                    </form>
                ) : (
                    // STAREA 2: Mesaj de Succes
                    <div className="flex flex-col items-center justify-center space-y-4 py-4">
                        <div className="h-16 w-16 bg-primary/10 dark:bg-primary/20 text-primary rounded-full flex items-center justify-center mb-2">
                            <CheckCircle2 className="h-8 w-8" />
                        </div>
                        <p className="text-center text-sm text-muted-foreground max-w-xs">
                            {dict.spam_notice}
                        </p>
                        <Button asChild className="w-full mt-4" variant="outline">
                            <Link href={`/${lang}/login`}>
                                {dict.success_back_btn}
                            </Link>
                        </Button>
                    </div>
                )}
        </AuthCardShell>
    )
}